import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth, logout } from "@/hooks/use-auth";
import { useAdminSites, useCreateSite, useUpdateSite, useDeleteSite, uploadSiteCover, Site } from "@/hooks/use-sites";
import { MapPin, ChevronLeft, Plus, Edit2, X, Archive, RotateCcw, Loader2, Image as ImageIcon, Trash2, Upload, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { BRANDS } from "@/lib/constants";

export default function AdminSites() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAuthLoading) {
      if (!user) {
        setLocation("/admin");
      } else if (user.role !== "admin") {
        setLocation("/engineer");
      }
    }
  }, [user, isAuthLoading, setLocation]);

  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState<string | "all">("all");

  const { data: sites, isLoading: isSitesLoading } = useAdminSites();
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  
  const [formData, setFormData] = useState<{
    name: string;
    brand: string;
    address: string;
    phone: string;
    email: string;
    contactName: string;
    coverImagePath: string | null;
  }>({ name: "", brand: "", address: "", phone: "", email: "", contactName: "", coverImagePath: null });

  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const filteredSites = (sites || [])
    .filter(s => (activeTab === "active" ? s.active : !s.active))
    .filter(s => brandFilter === "all" ? true : brandFilter === "none" ? (s.brand === "" || !s.brand) : s.brand === brandFilter)
    .filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.address.toLowerCase().includes(searchTerm.toLowerCase()));

  const openNew = () => {
    setEditingSite(null);
    setFormData({ name: "", brand: "", address: "", phone: "", email: "", contactName: "", coverImagePath: null });
    setPreviewUrl(null);
    setIsFormOpen(true);
  };

  const openEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      brand: site.brand || "",
      address: site.address,
      phone: site.phone || "",
      email: site.email || "",
      contactName: site.contactName || "",
      coverImagePath: site.coverImagePath,
    });
    setPreviewUrl(null);
    setIsFormOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (uploading) return;

    if (editingSite) {
      updateSite.mutate({ id: editingSite.id, data: formData }, {
        onSuccess: () => {
          toast({ title: "Site updated" });
          setIsFormOpen(false);
        },
        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
      });
    } else {
      createSite.mutate(formData, {
        onSuccess: () => {
          toast({ title: "Site created" });
          setIsFormOpen(false);
        },
        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
      });
    }
  };

  const handleArchive = (site: Site) => {
    if (confirm(`Are you sure you want to archive ${site.name}?`)) {
      deleteSite.mutate(site.id, {
        onSuccess: () => toast({ title: "Site archived" })
      });
    }
  };

  const handleUnarchive = (site: Site) => {
    updateSite.mutate({ id: site.id, data: { active: true } }, {
      onSuccess: () => toast({ title: "Site unarchived" })
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max file size is 5MB", variant: "destructive" });
      return;
    }
    
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast({ title: "Invalid format", description: "Only JPEG, PNG, and WebP are allowed", variant: "destructive" });
      return;
    }

    setUploading(true);
    // Create a local blob preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    try {
      const objectPath = await uploadSiteCover(file);
      setFormData(prev => ({ ...prev, coverImagePath: objectPath }));
      toast({ title: "Cover image uploaded" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/dashboard" className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 text-primary">
              <MapPin className="w-5 h-5" />
              <h1 className="font-bold text-lg text-slate-900">Sites Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600 hidden sm:inline-block">Admin: {user.name}</span>
            <button onClick={logout} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">Logout</button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex bg-white rounded-xl border border-slate-200 p-1 w-full max-w-sm">
            <button
              onClick={() => setActiveTab("active")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === "active" ? "bg-slate-100 text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Active Sites
            </button>
            <button
              onClick={() => setActiveTab("archived")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === "archived" ? "bg-slate-100 text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Archived
            </button>
          </div>

          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto">
            <select 
              value={brandFilter} 
              onChange={e => setBrandFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-max"
            >
              <option value="all">All Brands</option>
              <option value="none">No Brand</option>
              {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search sites..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <button onClick={openNew} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap shadow-sm">
              <Plus className="w-4 h-4" /> New Site
            </button>
          </div>
        </div>

        {isFormOpen && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> {editingSite ? "Edit Site" : "Create Site"}
              </h3>
              <button type="button" onClick={() => setIsFormOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Site Name <span className="text-red-500">*</span></label>
                      <input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" placeholder="e.g. Acme Corp HQ" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Brand</label>
                      <select value={formData.brand} onChange={e => setFormData({ ...formData, brand: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white">
                        <option value="">No Brand</option>
                        {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Site Address <span className="text-red-500">*</span></label>
                    <textarea required value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm resize-none" placeholder="Full address including postcode" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Contact Name</label>
                    <input value={formData.contactName} onChange={e => setFormData({ ...formData, contactName: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" placeholder="Site Manager / Contact Person" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Phone</label>
                      <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" placeholder="Contact number" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
                      <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" placeholder="Contact email" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Site Cover Image</label>
                <div className="flex items-start gap-4">
                  {(previewUrl || formData.coverImagePath) ? (
                    <div className="relative w-48 h-32 rounded-lg border border-slate-200 overflow-hidden group bg-slate-50">
                      <img src={previewUrl || `${import.meta.env.BASE_URL}${formData.coverImagePath!.replace(/^\//, "")}`} alt="Cover preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button type="button" onClick={() => { setFormData(prev => ({ ...prev, coverImagePath: null })); setPreviewUrl(null); }} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                      {uploading ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin mb-2" />
                          <span className="text-xs font-medium">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 mb-2" />
                          <span className="text-xs font-medium">No cover image</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 mb-3">Upload a photo of the site to help engineers identify it quickly. Supported formats: JPEG, PNG, WebP (max 5MB).</p>
                    <label className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50">
                      <Upload className="w-4 h-4" />
                      {uploading ? "Uploading..." : "Select Image"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} className="hidden" disabled={uploading} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                  Cancel
                </button>
                <button disabled={createSite.isPending || updateSite.isPending || uploading} type="submit" className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm">
                  {createSite.isPending || updateSite.isPending ? "Saving..." : "Save Site"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {isSitesLoading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filteredSites.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500">
              <MapPin className="w-12 h-12 text-slate-200 mb-3" />
              <p className="font-medium text-slate-900 mb-1">No sites found</p>
              <p className="text-sm">Create a site or adjust your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-4 sm:p-4 divide-y sm:divide-y-0 divide-slate-100">
              {filteredSites.map(site => (
                <div key={site.id} className="bg-white sm:border border-slate-200 sm:rounded-xl overflow-hidden sm:shadow-sm hover:shadow-md transition-shadow group flex flex-col">
                  {site.coverImagePath ? (
                    <div className="h-32 w-full bg-slate-100 relative">
                      <img src={`${import.meta.env.BASE_URL}${site.coverImagePath.replace(/^\//, "")}`} alt={site.name} className="w-full h-full object-cover" />
                      {!site.active && <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center"><span className="bg-black/60 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm">Archived</span></div>}
                    </div>
                  ) : (
                    <div className="h-2 w-full bg-primary/20"></div>
                  )}
                  
                  <div className="p-4 md:p-5 flex-1 flex flex-col">
                    <h3 className="font-bold text-slate-900 text-lg mb-1 flex items-center flex-wrap gap-2">
                      {site.name}
                      {site.brand ? <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full font-bold uppercase tracking-wider">{site.brand}</span> : null}
                    </h3>
                    <p className="text-sm text-slate-500 whitespace-pre-wrap flex-1 mb-4">{site.address}</p>
                    
                    <div className="space-y-2 mb-4">
                      {site.contactName && <p className="text-xs text-slate-600"><span className="font-semibold text-slate-400 mr-2 uppercase tracking-wider">Contact</span> {site.contactName}</p>}
                      {site.phone && <p className="text-xs text-slate-600"><span className="font-semibold text-slate-400 mr-2 uppercase tracking-wider">Phone</span> <a href={`tel:${site.phone}`} className="hover:text-primary">{site.phone}</a></p>}
                      {site.email && <p className="text-xs text-slate-600"><span className="font-semibold text-slate-400 mr-2 uppercase tracking-wider">Email</span> <a href={`mailto:${site.email}`} className="hover:text-primary">{site.email}</a></p>}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                      <div className="text-[10px] text-slate-400">
                        Added {new Date(site.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEdit(site)} className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-colors" title="Edit Site">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {site.active ? (
                          <button onClick={() => handleArchive(site)} className="p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600 rounded-lg transition-colors" title="Archive Site">
                            <Archive className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={() => handleUnarchive(site)} className="p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors" title="Restore Site">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
